import React, { useEffect, Fragment } from 'react';
import { Grid } from 'styled-css-grid';
import { useDispatch, useSelector } from 'react-redux';
import kebabCase from 'lodash/kebabCase';

import { Divider } from '@crm-framework/divider';
import { ReduxInject } from '@crm-framework/saga-injector';
import { Spacing } from '@crm-framework/spacing';
import { AccessDenied } from '@crm-framework/empty-state';

import { IsOnlineIcon } from '@crm-package/is-online-icon';

import {
  leadShortInfoActions,
  leadShortInfoReducer,
  STORE_NAME,
} from '../store';
import { getLeadShortInfoState } from '../utils';
import { watchShortLeadInfo } from '../sagas';

import { ValueOfKey, UserFullName, Wrapper, Header } from './styled';
import { ShortLeadInfoSkeleton } from './Skeleton';

interface IShortInfoProps {
  leadId: number;
}

export const LeadShortInfo: React.FC<IShortInfoProps> = ({ leadId }) => {
  const dispatch = useDispatch();

  const { leadInfo } = useSelector(getLeadShortInfoState);

  useEffect(() => {
    dispatch(leadShortInfoActions.leadLoad(leadId));

    return () => {
      dispatch(leadShortInfoActions.setInitLeadState());
    };
  }, []);

  if (leadInfo.isLoading) {
    return <ShortLeadInfoSkeleton />;
  }

  if (leadInfo.isPermissionError) {
    return <AccessDenied />;
  }

  if (!leadInfo.leadData) {
    return null;
  }

  const { isOnline, fullName, deviceType, ...rest } = leadInfo.leadData;

  return (
    <Wrapper>
      <Header>
        <IsOnlineIcon isOnline={isOnline} deviceType={deviceType} />
        <UserFullName title={fullName}>{fullName}</UserFullName>
      </Header>

      <Spacing mb={10}>
        <Divider>Personal Details</Divider>
      </Spacing>
      <Grid columns="min-content 1fr" gap="1px 3px">
        {Object.keys(rest).map((key) => (
          <Fragment key={kebabCase(key)}>
            <strong>{key}:</strong>
            <ValueOfKey isCanCopy title={rest[key]}>
              {rest[key]}
            </ValueOfKey>
          </Fragment>
        ))}
      </Grid>
    </Wrapper>
  );
};

export const LeadShortInfoWithInject: React.FC<IShortInfoProps> = (props) => (
  <ReduxInject
    keyName={STORE_NAME}
    reducer={leadShortInfoReducer}
    saga={watchShortLeadInfo}>
    <LeadShortInfo {...props} />
  </ReduxInject>
);
